import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, Button, Space, Spin, Typography, Tag, Modal, List, Divider, Badge, Tooltip } from 'antd';
import { message } from 'antd';
import { SendOutlined, CloseOutlined, MessageOutlined, PlusOutlined, BellOutlined, EyeOutlined, EyeInvisibleOutlined, ArrowLeftOutlined, WarningOutlined } from '@ant-design/icons';
import { useNotifications } from '../../hooks/use_notifications';
import './ChatBubbles.css';
import { EntityImage } from '../Entities/EntityImage';
import ChannelAvatar from './ChannelAvatar';
import MessageBubble from '../Entities/MessageBubble';
import { useAuth } from '../../context/AuthContext';
import useIsAdmin from '../../hooks/use_is_admin';
import EntityComms from '../Entities/EntityComms';
import { useApi } from '../../hooks/use_api';
import { useIsMobile } from '../../hooks/use_is_mobile';
import EmojiPicker, { insertAtCaret } from '../Entities/EmojiPicker';

const { TextArea } = Input;
const { Text } = Typography;

const SESSION_MESSAGE_LIMIT = 100;
const CHANNELS_ERROR = "Couldn't load your conversations.";

const msgCacheKey = (channelId) => `vellby:messages:${channelId}`;
const readMsgCache = (channelId) => {
    try { return JSON.parse(localStorage.getItem(msgCacheKey(channelId)) || 'null'); }
    catch { return null; }
};
const writeMsgCache = (channelId, messages) => {
    try { localStorage.setItem(msgCacheKey(channelId), JSON.stringify(messages)); }
    catch { }
};


function Messages() {
    const { entityId: entityIdParam } = useParams();
    const navigate = useNavigate();
    const { user, userPersonEntity } = useAuth();
    const api = useApi();
    const isMobile = useIsMobile();
    const { setPreference, fetchPreferences, markEntityRead, unreadByEntity } = useNotifications();
    const [notifPrefs, setNotifPrefs] = useState({});
    const [showHidden, setShowHidden] = useState(false);

    // Channels: one per connected context/relationship entity
    const [channels, setChannels] = useState([]);
    // The people connected to each relationship, by relationship id — a
    // relationship channel is shown as that club of people rather than by
    // its own name and picture (see channelDisplay below).
    const [members, setMembers] = useState({});
    const membersRequested = useRef(new Set());
    const activeEntityId = entityIdParam ? Number(entityIdParam) : null;

    const setActiveEntityId = (id) => {
        navigate(id ? `/messages/${id}` : '/messages', { replace: true });
    };
    const [loadingChannels, setLoadingChannels] = useState(true);
    // A failed load has to be distinguishable from a genuinely empty account:
    // both used to render "no channels yet", so a dropped DB port-forward
    // looked like having no conversations. `summaryStale` is the softer case —
    // counts failed but the per-channel cache still had something to show.
    const [channelsError, setChannelsError] = useState(null);
    const [summaryStale, setSummaryStale] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [loadingSession, setLoadingSession] = useState(false);

    // Chat state
    const [inputValue, setInputValue] = useState('');
    const [sending, setSending] = useState(false);

    // Thread state
    const [activeThreadId, setActiveThreadId] = useState(null);
    const [threadInputValue, setThreadInputValue] = useState('');
    const inputRef = useRef(null);
    const threadInputRef = useRef(null);
    const [sendingThread, setSendingThread] = useState(false);


    // Edit state — which message is open (the draft text lives inside MessageBubble)
    const [editingMessageId, setEditingMessageId] = useState(null);

    // New channel modal state
    const [newChannelModal, setNewChannelModal] = useState(false);
    const [newChannelEntity, setNewChannelEntity] = useState(null);
    const [newChannelInput, setNewChannelInput] = useState('');
    const [creatingChannel, setCreatingChannel] = useState(false);
    const [candidateEntities, setCandidateEntities] = useState([]);
    // People you could start a DM with: fetched once the picker opens (null =
    // not yet), filtered by the box at the top since a node can hold many.
    const [people, setPeople] = useState(null);
    const [pickerFilter, setPickerFilter] = useState('');
    const [startingDm, setStartingDm] = useState(null);

    // Full entity data + admin status for the active channel
    const [activeEntityFull, setActiveEntityFull] = useState(null);
    const { isAdmin } = useIsAdmin(activeEntityId);

    const messagesEndRef = useRef(null);
    const threadEndRef = useRef(null);

    const senderName = user?.name || user?.email || 'You';

    // ── Load channel tabs (single summary fetch — lazy-load messages per channel) ──
    useEffect(() => {
        if (!userPersonEntity?.id) return;
        let cancelled = false;
        // Reset per attempt so a retry that succeeds clears the previous error.
        let summaryFailed = false;
        setLoadingChannels(true);
        setChannelsError(null);
        setSummaryStale(false);
        api.get(`/api/entities/${userPersonEntity.id}/connected`)
            .then(async (data) => {
                if (cancelled) return [];
                const candidates = data.filter(c =>
                    c.connected_status === true &&
                    (c.entity.entity_type === 'context' || c.entity.entity_type === 'relationship')
                );
                setCandidateEntities(candidates.map(c => c.entity));
                if (candidates.length === 0) return [];

                const channelIds = candidates.map(c => `channel_${c.entity.id}`);
                // Still swallowed, because the cache fallback below is a real
                // offline feature — but remembered, so an empty result can be
                // reported as a failure rather than as "no conversations".
                const summaries = await api.post('/api/messages/summary', { channel_ids: channelIds })
                    .catch(() => { summaryFailed = true; return []; });
                const summaryByChannel = Object.fromEntries(summaries.map(s => [s.channel_id, s]));

                return candidates
                    .map(c => {
                        const channelId = `channel_${c.entity.id}`;
                        const summary = summaryByChannel[channelId] || { channel_id: channelId, message_count: 0 };
                        // Include the channel if the server knows about messages OR we have a
                        // cached history (offline-friendly: server may be empty but the user
                        // expects to still see their past channels).
                        if (summary.message_count > 0) {
                            return { entity: c.entity, sessionId: undefined, messages: undefined, summary, connectedPersons: [] };
                        }
                        const cached = readMsgCache(channelId);
                        if (Array.isArray(cached) && cached.length > 0) {
                            const last = cached[cached.length - 1];
                            return {
                                entity: c.entity,
                                sessionId: channelId,
                                messages: cached,
                                summary: {
                                    channel_id: channelId,
                                    message_count: cached.length,
                                    last_message_at: last?.timestamp,
                                    last_preview: (last?.content || '').slice(0, 150),
                                    last_sender_name: last?.sender_name,
                                },
                                connectedPersons: [],
                            };
                        }
                        return null;
                    })
                    .filter(Boolean);
            })
            .then(tabs => {
                if (cancelled) return;
                // Nothing to show AND we know a request failed — that is an
                // error, not an empty account.
                if (summaryFailed && tabs.length === 0) {
                    setChannelsError(CHANNELS_ERROR);
                    return;
                }
                if (summaryFailed) setSummaryStale(true);
                setChannels(tabs);
                if (tabs.length > 0 && !entityIdParam) setActiveEntityId(tabs[0].entity.id);
            })
            .catch(err => {
                // /connected itself failed; there is no cache fallback for the
                // channel list, so this is always an error. ApiError.message is
                // FastAPI's `detail` verbatim — a raw psycopg2 traceback on a
                // 500 — so it goes to the console, never to the panel.
                if (cancelled) return;
                console.error('Could not load channels:', err);
                setChannelsError(CHANNELS_ERROR);
            })
            .finally(() => { if (!cancelled) setLoadingChannels(false); });
        return () => { cancelled = true; };
    }, [userPersonEntity?.id, reloadKey]);

    // Counts are stale but cached history carried the sidebar — worth saying
    // once, not worth blocking the page for.
    useEffect(() => {
        if (summaryStale) message.warning('Showing cached conversations — could not reach the server.');
    }, [summaryStale]);

    // ── Relationship members ─────────────────────────────────────────────────
    // Fetched once per relationship (a failed fetch is retried the next time
    // the list changes); until they arrive the row falls back to the
    // relationship's own name and picture.
    useEffect(() => {
        [...candidateEntities, ...(newChannelEntity ? [newChannelEntity] : [])]
            .filter(e => e?.entity_type === 'relationship' && !membersRequested.current.has(e.id))
            .forEach(e => {
                membersRequested.current.add(e.id);
                api.get(`/api/entities/${e.id}/connected`)
                    .then(rows => {
                        const people = rows
                            .filter(c => c.connected_status === true && c.entity.entity_type === 'person')
                            .map(c => c.entity);
                        setMembers(prev => ({ ...prev, [e.id]: people }));
                    })
                    .catch(() => membersRequested.current.delete(e.id));
            });
    }, [candidateEntities, newChannelEntity, api]);

    // Everyone in a relationship except the viewer — the club a relationship
    // channel is drawn as. Empty while members are still loading.
    const othersIn = (entity) => (members[entity?.id] || []).filter(p => p.id !== userPersonEntity?.id);

    // What a channel shows. A context is its own name and picture. A
    // relationship is the people in it other than the viewer: one person is
    // drawn alone under their own name; several are drawn as a stacked avatar
    // group under the relationship's name.
    const channelDisplay = (entity) => {
        if (entity?.entity_type !== 'relationship') return { name: entity?.name, people: null };
        const people = othersIn(entity);
        return { name: people.length === 1 ? people[0].name : entity.name, people };
    };

    // ── Detect first-time visit to an entity channel ─────────────────────────
    useEffect(() => {
        if (loadingChannels || !activeEntityId) return;
        const alreadyLoaded = channels.find(c => c.entity.id === activeEntityId);
        if (alreadyLoaded) return;
        // Entity not in the sidebar yet — fetch its info and show the create-channel modal
        api.get(`/api/entities/${activeEntityId}`)
            .then(entity => {
                if (!entity) return;
                setNewChannelEntity(entity);
                setNewChannelModal(true);
            })
            .catch(() => {});
    }, [loadingChannels, activeEntityId]);

    // ── Create channel + send first message ──────────────────────────────────
    const handleCreateChannel = async () => {
        const content = newChannelInput.trim();
        if (!content || !newChannelEntity || !user?.id) return;
        setCreatingChannel(true);
        try {
            // Find or create the channel record
            const channel = await api.get(`/api/channels/entity/${newChannelEntity.id}`);

            const msg = {
                message_id: crypto.randomUUID(),
                role: 'user',
                content,
                timestamp: new Date().toISOString(),
                sender_id: user.id,
                sender_name: senderName,
                reply_to: null,
            };
            const saved = await api.post(`/api/messages/${encodeURIComponent(channel.channel_id)}`, msg);

            setChannels(prev => {
                const summary = {
                    channel_id: channel.channel_id,
                    message_count: 1,
                    last_message_at: saved.timestamp,
                    last_preview: (saved.content || '').slice(0, 150),
                    last_sender_name: saved.sender_name,
                };
                const exists = prev.find(c => c.entity.id === newChannelEntity.id);
                if (exists) {
                    return prev.map(c => c.entity.id === newChannelEntity.id
                        ? {
                            ...c,
                            sessionId: channel.channel_id,
                            messages: [...(c.messages || []), saved],
                            summary: {
                                ...(c.summary || summary),
                                message_count: (c.summary?.message_count || 0) + 1,
                                last_message_at: saved.timestamp,
                                last_preview: summary.last_preview,
                                last_sender_name: saved.sender_name,
                            },
                        }
                        : c);
                }
                return [...prev, { entity: newChannelEntity, sessionId: channel.channel_id, messages: [saved], summary, connectedPersons: [] }];
            });
            // Backend seeds this channel's NotificationPreference as enabled on first
            // creation (seed_unmuted_preferences_for_new_channel) — mirror that here so
            // it doesn't render as muted until the next full preferences refetch.
            setNotifPrefs(prev => ({ ...prev, [newChannelEntity.id]: true }));
            setNewChannelModal(false);
            setNewChannelInput('');
            setNewChannelEntity(null);
        } catch (err) {
            console.error('handleCreateChannel error:', err);
            message.error(`Failed to create channel: ${err.message}`);
        } finally {
            setCreatingChannel(false);
        }
    };

    // ── Load notification preferences once on mount ──────────────────────────
    useEffect(() => {
        if (!user?.id) return;
        fetchPreferences().then(prefs => setNotifPrefs(prefs));
    }, [user?.id]);

    // ── Auto-mark-read when switching to a channel ────────────────────────────
    useEffect(() => {
        if (!activeEntityId || !user?.id) return;
        markEntityRead(activeEntityId);
    }, [activeEntityId, user?.id]);

    // Keep the active channel visible if it's muted (user navigated to a hidden one).
    // Channels are muted by default — any value other than explicit `true` is hidden.
    useEffect(() => {
        if (activeEntityId && notifPrefs[activeEntityId] !== true) setShowHidden(true);
    }, [activeEntityId, notifPrefs]);

    // ── Fetch full entity data (hangout state) ────────────────────────────────
    useEffect(() => {
        if (!activeEntityId) { setActiveEntityFull(null); return; }
        api.get(`/api/entities/${activeEntityId}`)
            .then(data => setActiveEntityFull(data))
            .catch(() => setActiveEntityFull(null));
    }, [activeEntityId]);

    // ── Hydrate session + messages when tab is selected (lazy, stale-while-revalidate) ──
    // Waits for the channel list: on a direct load of /messages/<id> this used
    // to run first, write the fetched messages into an empty list (a no-op),
    // and never re-run once the list arrived — leaving "Select a channel".
    useEffect(() => {
        if (loadingChannels || !activeEntityId || !user?.id) return;
        const already = channels.find(c => c.entity.id === activeEntityId);
        if (!already) return; // not a channel yet — the first-visit effect offers to create it
        if (already.messages !== undefined) return; // already hydrated

        const channelId = `channel_${activeEntityId}`;
        const cached = readMsgCache(channelId);
        const haveCache = Array.isArray(cached) && cached.length > 0;

        // Stale-while-revalidate: paint cached messages immediately so the chat
        // is responsive on revisit. Only show the spinner when we have nothing.
        if (haveCache) {
            setChannels(prev => prev.map(c =>
                c.entity.id === activeEntityId
                    ? { ...c, sessionId: channelId, messages: cached }
                    : c
            ));
        } else {
            setLoadingSession(true);
        }

        api.get(`/api/channels/entity/${activeEntityId}`)
            .then(channel => {
                if (!channel) return;
                // The newest page of the channel (oldest first). A full page means
                // there is earlier history to page back into with `before`.
                api.get(`/api/messages/${encodeURIComponent(channel.channel_id)}`)
                    .then(msgs => {
                        setChannels(prev => prev.map(c =>
                            c.entity.id === activeEntityId
                                ? { ...c, sessionId: channel.channel_id, messages: msgs, hasEarlier: msgs.length >= SESSION_MESSAGE_LIMIT }
                                : c
                        ));
                    })
                    .catch(() => {
                        if (!haveCache) {
                            setChannels(prev => prev.map(c =>
                                c.entity.id === activeEntityId
                                    ? { ...c, sessionId: channel.channel_id, messages: [] }
                                    : c
                            ));
                        }
                    });
            })
            .catch(() => {})
            .finally(() => setLoadingSession(false));
    }, [activeEntityId, user?.id, loadingChannels]);

    // ── Scroll to bottom on new messages ─────────────────────────────────────
    const activeChannel = channels.find(c => c.entity.id === activeEntityId) || null;

    const newestSeenRef = useRef(null);
    useEffect(() => {
        const msgs = activeChannel?.messages;
        const newest = msgs?.length ? msgs[msgs.length - 1].message_id : null;  // UUIDs: unique across channels
        // Only a new newest message pulls the view down — paging earlier history
        // in at the top, or a refreshed reaction, must not yank the reader away.
        if (newest === newestSeenRef.current) return;
        newestSeenRef.current = newest;
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [activeChannel?.messages]);

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [activeChannel?.messages, activeThreadId]);

    // The active channel's messages, readable from inside the poll's timer
    // without re-arming the interval on every change.
    const heldMessagesRef = useRef([]);
    useEffect(() => { heldMessagesRef.current = activeChannel?.messages || []; }, [activeChannel?.messages]);

    // ── Poll for messages from other users every 5s ───────────────────────────
    useEffect(() => {
        if (!activeChannel?.sessionId) return;
        const channelId = activeChannel.sessionId;
        const base = `/api/messages/${encodeURIComponent(channelId)}`;
        const fetchNew = async () => {
            // The newest page: new messages, and the current reactions on the
            // visible tail. If a whole page comes back with nothing we hold, more
            // than a page landed since the last poll — page forward from our
            // newest message with `after` so nothing in between is lost.
            const page = await api.get(base);
            if (!Array.isArray(page)) return null;
            const held = heldMessagesRef.current;
            const heldIds = new Set(held.map(m => m.message_id).filter(Boolean));
            if (page.length < SESSION_MESSAGE_LIMIT || held.length === 0 || page.some(m => heldIds.has(m.message_id))) return page;
            let data = page;
            let cursor = held[held.length - 1].message_id;
            for (let hops = 0; hops < 10 && cursor; hops++) {
                const more = await api.get(`${base}?after=${encodeURIComponent(cursor)}`).catch(() => null);
                if (!Array.isArray(more) || more.length === 0) break;
                data = data.concat(more);
                cursor = more.length < SESSION_MESSAGE_LIMIT ? null : more[more.length - 1].message_id;
            }
            return data;
        };
        const poll = setInterval(() => {
            fetchNew()
                .then(data => {
                    if (!Array.isArray(data)) return;
                    setChannels(prev => prev.map(c => {
                        if (c.entity.id !== activeEntityId) return c;
                        const existingIds = new Set(c.messages.map(m => m.message_id).filter(Boolean));
                        const newMsgs = data.filter(m => m.message_id && !existingIds.has(m.message_id));
                        // Other people's reactions arrive on messages we already have;
                        // swap them in only when they changed so an idle poll re-renders nothing.
                        const serverReactions = new Map(data.map(m => [m.message_id, m.reactions || []]));
                        let reactionsChanged = false;
                        const refreshed = c.messages.map(m => {
                            const fresh = serverReactions.get(m.message_id);
                            if (!fresh || JSON.stringify(fresh) === JSON.stringify(m.reactions || [])) return m;
                            reactionsChanged = true;
                            return { ...m, reactions: fresh };
                        });
                        if (newMsgs.length === 0) return reactionsChanged ? { ...c, messages: refreshed } : c;
                        const merged = [...refreshed, ...newMsgs]
                            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                        const last = merged[merged.length - 1];
                        return {
                            ...c,
                            messages: merged,
                            summary: {
                                ...(c.summary || { channel_id: c.sessionId, message_count: 0 }),
                                message_count: (c.summary?.message_count || 0) + newMsgs.length,
                                last_message_at: last?.timestamp,
                                last_preview: (last?.content || '').slice(0, 150),
                                last_sender_name: last?.sender_name,
                            },
                        };
                    }));
                })
                .catch(() => {});
        }, 5000);
        return () => clearInterval(poll);
    }, [activeChannel?.sessionId, activeEntityId]);

    // ── Sync messages to localStorage whenever channels state changes ─────────
    useEffect(() => {
        channels.forEach(ch => {
            if (ch.sessionId && Array.isArray(ch.messages) && ch.messages.length > 0) {
                writeMsgCache(ch.sessionId, ch.messages);
            }
        });
    }, [channels]);

    // ── Message helpers ───────────────────────────────────────────────────────
    const collectThread = (rootId, msgs) => {
        const result = [];
        const queue = [rootId];
        while (queue.length > 0) {
            const parentId = queue.shift();
            const children = msgs.filter(m => m.reply_to === parentId);
            children.forEach(c => { result.push(c); queue.push(c.message_id); });
        }
        return result.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    };

    const [loadingEarlier, setLoadingEarlier] = useState(false);
    const loadEarlier = (channel) => {
        const oldest = channel.messages?.[0];
        if (!channel.sessionId || !oldest?.message_id || loadingEarlier) return;
        setLoadingEarlier(true);
        api.get(`/api/messages/${encodeURIComponent(channel.sessionId)}?before=${encodeURIComponent(oldest.message_id)}`)
            .then(older => {
                if (!Array.isArray(older)) return;
                setChannels(prev => prev.map(c => {
                    if (c.sessionId !== channel.sessionId) return c;
                    const have = new Set(c.messages.map(m => m.message_id));
                    return {
                        ...c,
                        messages: [...older.filter(m => !have.has(m.message_id)), ...c.messages],
                        hasEarlier: older.length >= SESSION_MESSAGE_LIMIT,
                    };
                }));
            })
            .catch(() => {})
            .finally(() => setLoadingEarlier(false));
    };

    const appendMessageToBackend = (channelId, msg) => {
        api.post(`/api/messages/${encodeURIComponent(channelId)}`, msg).catch(() => {});
    };

    const sendMessage = (content, replyTo, inputSetter, loadingSetter) => {
        if (!content.trim() || !activeChannel?.sessionId) return;
        const msg = {
            message_id: crypto.randomUUID(),
            role: 'user',
            content: content.trim(),
            timestamp: new Date().toISOString(),
            sender_id: user.id,
            sender_name: senderName,
            reply_to: replyTo || null,
        };
        inputSetter('');
        loadingSetter(true);
        setChannels(prev => prev.map(c =>
            c.entity.id === activeEntityId
                ? {
                    ...c,
                    messages: [...c.messages, msg],
                    summary: {
                        ...(c.summary || { channel_id: c.sessionId, message_count: 0 }),
                        message_count: (c.summary?.message_count || 0) + 1,
                        last_message_at: msg.timestamp,
                        last_preview: (msg.content || '').slice(0, 150),
                        last_sender_name: msg.sender_name,
                    },
                }
                : c
        ));
        appendMessageToBackend(activeChannel.sessionId, msg);
        loadingSetter(false);
    };

    const saveEdit = (channelId, messageId, content) => {
        const trimmed = (content ?? '').trim();
        setEditingMessageId(null);
        if (!trimmed) return;
        setChannels(prev => prev.map(c => c.entity.id !== activeEntityId ? c : {
            ...c,
            messages: c.messages.map(m => m.message_id === messageId ? { ...m, content: trimmed } : m),
        }));
        api.patch(`/api/messages/${encodeURIComponent(channelId)}/${encodeURIComponent(messageId)}`, { content: trimmed }).catch(() => {});
    };

    const deleteMsg = (channelId, messageId) => {
        setChannels(prev => prev.map(c => c.entity.id !== activeEntityId ? c : {
            ...c,
            messages: c.messages.filter(m => m.message_id !== messageId),
        }));
        if (activeThreadId === messageId) setActiveThreadId(null);
        api.del(`/api/messages/${encodeURIComponent(channelId)}/${encodeURIComponent(messageId)}`).catch(() => {});
    };

    const setMessageReactions = (messageId, update) => {
        setChannels(prev => prev.map(c => c.entity.id !== activeEntityId ? c : {
            ...c,
            messages: c.messages.map(m => m.message_id === messageId
                ? { ...m, reactions: typeof update === 'function' ? update(m.reactions || []) : update }
                : m),
        }));
    };

    // Optimistic toggle; the server's reply (the message's full reaction list) then wins.
    const toggleReaction = (channelId, messageId, emoji) => {
        if (!user?.id) return;
        const msg = activeChannel?.messages.find(m => m.message_id === messageId);
        const mine = (msg?.reactions || []).some(r => r.emoji === emoji && r.user_id === user.id);
        setMessageReactions(messageId, rs => mine
            ? rs.filter(r => !(r.emoji === emoji && r.user_id === user.id))
            : [...rs, { emoji, user_id: user.id }]);
        const path = `/api/messages/${encodeURIComponent(channelId)}/${encodeURIComponent(messageId)}/reactions`;
        const request = mine
            ? api.del(path, { params: { user_id: user.id, emoji } })
            : api.post(path, { emoji }, { params: { user_id: user.id } });
        request
            .then(reactions => { if (Array.isArray(reactions)) setMessageReactions(messageId, reactions); })
            .catch(() => message.error('Could not update reaction'));
    };

    const isOwn = msg => msg.sender_id === user?.id;

    // ── Message bubble renderer (thread panel; the list uses MessageBubble inline) ──
    const renderBubble = (msg) => (
        <MessageBubble
            key={msg.message_id || msg.timestamp}
            msg={msg}
            own={isOwn(msg)}
            variantClass="assistant"
            editable
            isEditing={editingMessageId === msg.message_id}
            onStartEdit={m => setEditingMessageId(m.message_id)}
            onSaveEdit={(id, content) => saveEdit(activeChannel.sessionId, id, content)}
            onCancelEdit={() => setEditingMessageId(null)}
            onDelete={id => deleteMsg(activeChannel.sessionId, id)}
            currentUserId={user?.id}
            onToggleReaction={(id, emoji) => toggleReaction(activeChannel.sessionId, id, emoji)}
        />
    );

    // ── Keyboard handlers ─────────────────────────────────────────────────────
    const handleKeyPress = e => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(inputValue, null, setInputValue, setSending); }
    };
    const handleThreadKeyPress = e => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(threadInputValue, activeThreadId, setThreadInputValue, setSendingThread); }
    };

    const handleNotifToggle = (checked) => {
        setNotifPrefs(prev => ({ ...prev, [activeEntityId]: checked }));
        setPreference(activeEntityId, checked).catch(() =>
            setNotifPrefs(prev => ({ ...prev, [activeEntityId]: !checked }))
        );
    };

    const unchanneledEntities = candidateEntities.filter(e => !channels.find(ch => ch.entity.id === e.id));

    const sortedChannels = [...channels].sort((a, b) => {
        if (a.entity.entity_type === b.entity.entity_type) return 0;
        return a.entity.entity_type === 'relationship' ? -1 : 1;
    });
    const isMuted = (entityId) => notifPrefs[entityId] !== true;
    const visibleChannels = sortedChannels.filter(c => !isMuted(c.entity.id));
    const hiddenChannels = sortedChannels.filter(c => isMuted(c.entity.id));

    const renderChannelRow = (ch, idx, arr, { muted = false } = {}) => {
        const prevType = arr[idx - 1]?.entity.entity_type;
        const showDivider = idx > 0 && prevType !== ch.entity.entity_type;
        const isActive = ch.entity.id === activeEntityId;
        const { name: displayName, people } = channelDisplay(ch.entity);
        return (
            <div key={ch.entity.id}>
                {showDivider && <Divider style={{ margin: '4px 0' }} />}
                <div
                    onClick={() => { setActiveEntityId(ch.entity.id); setActiveThreadId(null); }}
                    style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        background: isActive ? 'var(--bg-primary)' : 'transparent',
                        borderLeft: isActive ? '3px solid #1890ff' : '3px solid transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        opacity: muted ? 0.55 : 1,
                    }}
                >
                    {/* Picture then name, as on the browse page — no type dot, no
                        initials chip. The picture still opens the entity; for a
                        relationship each person's picture opens that person. */}
                    <ChannelAvatar
                        entity={ch.entity}
                        people={people}
                        size={24}
                        onOpen={target => navigate(`/entity/${target.id}`)}
                    />
                    <Text ellipsis style={{ flex: 1, fontSize: 13, fontWeight: isActive ? 600 : 400 }}>
                        {displayName}
                    </Text>
                    <Badge count={unreadByEntity[ch.entity.id] || 0} size="small" />
                    {muted && <BellOutlined style={{ fontSize: 10, color: 'var(--text-secondary)' }} />}
                </div>
            </div>
        );
    };

    const openNewChannelModal = () => {
        setNewChannelEntity(null);
        setNewChannelInput('');
        setPickerFilter('');
        setNewChannelModal(true);
    };

    useEffect(() => {
        if (!newChannelModal || people !== null) return;
        api.get('/api/entities', { params: { entity_type: 'person' } })
            .then(data => setPeople(Array.isArray(data) ? data : []))
            .catch(() => setPeople([]));
    }, [newChannelModal, people, api]);

    // Everyone except me and anyone I already have a direct message with —
    // that conversation already appears above as the person's own row.
    const dmPeerIds = new Set(
        candidateEntities.filter(e => e.attributes?.is_dm).flatMap(e => othersIn(e).map(p => p.id))
    );
    const matchesFilter = name => !pickerFilter || (name || '').toLowerCase().includes(pickerFilter.toLowerCase());
    const pickableGroups = unchanneledEntities.filter(e => matchesFilter(channelDisplay(e).name));
    // Pending people — local invites nobody has claimed yet, or signups not yet
    // approved — are listed but can't be picked: the backend refuses a new DM
    // with them. Sorted to the end.
    const isPendingPerson = p => !p.is_remote && (!p.user_id || p.registration_approved === false);
    const pickablePeople = (people || [])
        .filter(p => p.id !== userPersonEntity?.id && !dmPeerIds.has(p.id) && matchesFilter(p.name))
        .sort((a, b) => isPendingPerson(a) - isPendingPerson(b));

    // Same call the person page's "Start messaging" makes: find-or-create the
    // DM wrapper, then continue into the first-message step with it selected.
    const startDmWith = async (person) => {
        if (!userPersonEntity?.id) return;
        setStartingDm(person.id);
        try {
            const res = await api.post('/api/connections/dm', {
                requester_person_id: userPersonEntity.id,
                target_person_id: person.id,
            });
            const wrapper = res.entity;
            // Its only other member is known without a fetch.
            membersRequested.current.add(wrapper.id);
            setMembers(prev => ({ ...prev, [wrapper.id]: [person] }));
            setCandidateEntities(prev => (prev.some(e => e.id === wrapper.id) ? prev : [...prev, wrapper]));
            setNewChannelEntity(wrapper);
        } catch (err) {
            message.error(err?.message || 'Could not start a conversation');
        } finally {
            setStartingDm(null);
        }
    };

    // On mobile there's only room for one pane at a time — pick which one based
    // on where the user has navigated to, instead of the desktop's side-by-side layout.
    const mobileView = !activeEntityId ? 'list' : (activeThreadId ? 'thread' : 'chat');
    const showSidebar = !isMobile || mobileView === 'list';
    const showChatArea = !isMobile || mobileView !== 'list';
    const showMainPane = !isMobile || mobileView === 'chat';
    const showThreadPane = !!activeThreadId && (!isMobile || mobileView === 'thread');
    const mobileChatHeader = activeChannel ? channelDisplay(activeChannel.entity) : null;

    // ── Render ────────────────────────────────────────────────────────────────
    if (loadingChannels) {
        // Same gutters and height as the loaded frame below, so the spinner sits
        // where the panel will be instead of jumping down once channels arrive.
        return (
            <div style={{ padding: isMobile ? 0 : '32px 20px 48px', maxWidth: isMobile ? 'none' : 1200, margin: '0 auto', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: isMobile ? 'calc(100vh - 52px)' : 'calc((100vh - 64px) * 0.8)' }}>
                    <Spin />
                </div>
            </div>
        );
    }

    const createChannelModal = (
        <Modal
            title={
                <Space>
                    <MessageOutlined />
                    <span>Start a new channel</span>
                </Space>
            }
            open={newChannelModal}
            onCancel={() => { setNewChannelModal(false); navigate('/messages', { replace: true }); }}
            onOk={handleCreateChannel}
            okText="Send"
            okButtonProps={{ disabled: !newChannelEntity || !newChannelInput.trim(), loading: creatingChannel }}
            destroyOnClose
        >
            {!newChannelEntity ? (
                <>
                    <Input
                        allowClear
                        placeholder="Filter by name"
                        value={pickerFilter}
                        onChange={e => setPickerFilter(e.target.value)}
                        style={{ marginBottom: 8 }}
                    />
                    {/* Rows are picture then name, as in the side panel and on the
                        browse page. Groups (contexts and relationships) first; then
                        people you have no direct message with yet. */}
                    {pickableGroups.length > 0 && (
                        <List
                            dataSource={pickableGroups}
                            renderItem={entity => {
                                const { name, people } = channelDisplay(entity);
                                return (
                                    <List.Item onClick={() => setNewChannelEntity(entity)} style={{ cursor: 'pointer', padding: '8px 4px' }}>
                                        <Space>
                                            <ChannelAvatar entity={entity} people={people} size={24} />
                                            <Text>{name}</Text>
                                            <Tag style={{ fontSize: 11 }}>{entity.entity_type}</Tag>
                                        </Space>
                                    </List.Item>
                                );
                            }}
                        />
                    )}
                    {people === null ? (
                        <Spin size="small" style={{ display: 'block', margin: '12px auto' }} />
                    ) : pickablePeople.length > 0 ? (
                        <>
                            <Divider plain style={{ margin: '8px 0', fontSize: 12 }}>People</Divider>
                            <List
                                dataSource={pickablePeople}
                                renderItem={person => {
                                    const pending = isPendingPerson(person);
                                    return (
                                        <List.Item
                                            onClick={() => !pending && !startingDm && startDmWith(person)}
                                            style={{
                                                cursor: pending ? 'not-allowed' : 'pointer',
                                                padding: '8px 4px',
                                                opacity: pending || (startingDm && startingDm !== person.id) ? 0.5 : 1,
                                            }}
                                        >
                                            <Space>
                                                <EntityImage entity={person} size={24} />
                                                <Text>{person.name}</Text>
                                                {pending && (
                                                    <Tooltip title={person.user_id ? 'Awaiting approval — you can message them once they are approved' : "Hasn't joined yet — you can message them once they have"}>
                                                        <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>Pending</Tag>
                                                    </Tooltip>
                                                )}
                                                {startingDm === person.id && <Spin size="small" />}
                                            </Space>
                                        </List.Item>
                                    );
                                }}
                            />
                        </>
                    ) : null}
                    {people !== null && pickableGroups.length === 0 && pickablePeople.length === 0 && (
                        <Text type="secondary">
                            {pickerFilter ? 'Nobody matches that name.' : 'Every group and person you can message already has a channel.'}
                        </Text>
                    )}
                </>
            ) : (
                <>
                    <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
                        Opening channel for <strong>{channelDisplay(newChannelEntity).name}</strong>.{' '}
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setNewChannelEntity(null)}>Change</Button>
                    </p>
                    <TextArea
                        autoFocus
                        value={newChannelInput}
                        onChange={e => setNewChannelInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCreateChannel(); } }}
                        placeholder="Type your first message..."
                        autoSize={{ minRows: 3, maxRows: 8 }}
                    />
                </>
            )}
        </Modal>
    );

    // Before the empty state, not merged into it: "we could not load this" and
    // "you have nothing yet" are different answers and only one of them is
    // worth a retry button.
    if (channelsError) {
        return (
            <>
                {createChannelModal}
                <div style={{ padding: isMobile ? 0 : '32px 20px 48px', maxWidth: isMobile ? 'none' : 1200, margin: '0 auto', boxSizing: 'border-box' }}>
                    <div style={{ border: isMobile ? 'none' : '1px solid var(--border-color)', borderRadius: isMobile ? 0 : 8, overflow: 'hidden', height: isMobile ? 'calc(100vh - 52px)' : 'calc((100vh - 64px) * 0.8)' }}>
                        <div className="empty-state" style={{ height: '100%', padding: '0 24px', textAlign: 'center' }}>
                            <WarningOutlined style={{ fontSize: 32, opacity: 0.4 }} />
                            <Text type="secondary" style={{ marginTop: 16 }}>{channelsError}</Text>
                            <Text type="secondary" style={{ marginTop: 4, fontSize: 12 }}>
                                Your conversations are safe — this is a connection problem, not missing data.
                            </Text>
                            <Button style={{ marginTop: 16 }} onClick={() => setReloadKey(k => k + 1)}>
                                Try again
                            </Button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
        {createChannelModal}
        {/* Desktop gutters match .browse-page (32px 20px 48px, centred at 1200) so the
            frame isn't flush against the nav and the window edge. The height sits on the
            frame itself, so the padding grows the page instead of shrinking the chat. */}
        <div style={{ padding: isMobile ? 0 : '32px 20px 48px', maxWidth: isMobile ? 'none' : 1200, margin: '0 auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', gap: 0, border: isMobile ? 'none' : '1px solid var(--border-color)', borderRadius: isMobile ? 0 : 8, overflow: 'hidden', height: isMobile ? 'calc(100vh - 52px)' : 'calc((100vh - 64px) * 0.8)' }}>

            {/* ── Left sidebar: channel tabs ── */}
            {showSidebar && (
            <div style={{
                width: isMobile ? '100%' : 220,
                borderRight: isMobile ? 'none' : '1px solid var(--border-color)',
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--bg-secondary)',
                flexShrink: 0,
            }}>
                <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Channels</Text>
                    <Button type="text" size="small" icon={<PlusOutlined />} onClick={openNewChannelModal} />
                </div>
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {hiddenChannels.length > 0 && (
                        <div style={{ borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                            <div
                                onClick={() => setShowHidden(s => !s)}
                                style={{
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 11,
                                    color: 'var(--text-secondary)',
                                }}
                            >
                                {showHidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                <span>{showHidden ? 'Hide muted' : `Show muted (${hiddenChannels.length})`}</span>
                            </div>
                            {showHidden && hiddenChannels.map((ch, idx, arr) => renderChannelRow(ch, idx, arr, { muted: true }))}
                        </div>
                    )}
                    <div style={{ flex: 1 }}>
                        {channels.length === 0 ? (
                            <Text type="secondary" style={{ display: 'block', padding: '16px 12px', fontSize: 12 }}>
                                No channels yet — tap + to start one.
                            </Text>
                        ) : (
                            visibleChannels.map((ch, idx, arr) => renderChannelRow(ch, idx, arr))
                        )}
                    </div>
                </div>
            </div>
            )}

            {/* ── Main chat area ── */}
            {showChatArea && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {isMobile && mobileView === 'chat' && activeChannel && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--border-color)',
                        background: 'var(--bg-secondary)',
                        flexShrink: 0,
                    }}>
                        <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => setActiveEntityId(null)} />
                        <ChannelAvatar entity={activeChannel.entity} people={mobileChatHeader?.people} size={22} />
                        <Text strong ellipsis style={{ fontSize: 13, flex: 1 }}>{mobileChatHeader?.name}</Text>
                    </div>
                )}
                {loadingSession ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <Spin />
                    </div>
                ) : !activeChannel?.sessionId ? (
                    <div className="empty-state" style={{ flex: 1 }}>
                        <MessageOutlined style={{ fontSize: 28, opacity: 0.3 }} />
                        <Text type="secondary" style={{ marginTop: 12, fontSize: 13 }}>
                            {channels.length === 0
                                ? 'No channels yet — click + to start a conversation.'
                                : 'Select a channel'}
                        </Text>
                    </div>
                ) : (
                    <>
                        {/* ── Comms panel ── */}
                        {activeEntityFull && (
                            <EntityComms
                                entityId={activeEntityId}
                                entity={activeEntityFull}
                                isAdmin={isAdmin}
                                notifEnabled={notifPrefs[activeEntityId] === true}
                                onToggleNotif={handleNotifToggle}
                                messagesPath={`/entity/${activeEntityId}`}
                                style={{ margin: 0, borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none', flexShrink: 0 }}
                            />
                        )}

                        {/* Messages + thread panel */}
                        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

                            {/* Main message list */}
                            {showMainPane && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <div className="messages-container" style={{ flex: 1 }}>
                                    {activeChannel.messages.length === 0 ? (
                                        <div className="empty-state">
                                            <MessageOutlined style={{ fontSize: 28, opacity: 0.3 }} />
                                            <Text type="secondary" style={{ marginTop: 12, fontSize: 13 }}>
                                                No messages yet — say hello!
                                            </Text>
                                        </div>
                                    ) : (<>
                                        {activeChannel.hasEarlier && (
                                            <div style={{ textAlign: 'center', padding: '4px 0' }}>
                                                <Button type="link" size="small" loading={loadingEarlier} onClick={() => loadEarlier(activeChannel)}>
                                                    Load earlier messages
                                                </Button>
                                            </div>
                                        )}
                                        {activeChannel.messages
                                            .filter(m => !m.reply_to)
                                            .map(msg => {
                                                const replyCount = activeChannel.messages.filter(m => m.reply_to === msg.message_id).length;
                                                const isActive = activeThreadId === msg.message_id;
                                                return (
                                                    <MessageBubble
                                                        key={msg.message_id || msg.timestamp}
                                                        msg={msg}
                                                        own={isOwn(msg)}
                                                        variantClass="assistant"
                                                        active={isActive}
                                                        editable
                                                        isEditing={editingMessageId === msg.message_id}
                                                        onStartEdit={m => setEditingMessageId(m.message_id)}
                                                        onSaveEdit={(id, content) => saveEdit(activeChannel.sessionId, id, content)}
                                                        onCancelEdit={() => setEditingMessageId(null)}
                                                        onDelete={id => deleteMsg(activeChannel.sessionId, id)}
                                                        currentUserId={user?.id}
                                                        onToggleReaction={(id, emoji) => toggleReaction(activeChannel.sessionId, id, emoji)}
                                                        replyCount={replyCount}
                                                        onToggleThread={() => setActiveThreadId(isActive ? null : msg.message_id)}
                                                    />
                                                );
                                            })}
                                    </>)}
                                    <div ref={messagesEndRef} />
                                </div>

                                {!activeThreadId && (
                                    <div className="input-container" style={{ padding: '8px', borderTop: '1px solid var(--border-color)' }}>
                                        <EmojiPicker
                                            disabled={sending}
                                            onSelect={emoji => insertAtCaret(inputRef, inputValue, setInputValue, emoji)}
                                        />
                                        <TextArea
                                            ref={inputRef}
                                            value={inputValue}
                                            onChange={e => setInputValue(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder={activeEntityFull?.is_remote ? `Message @${activeEntityFull.home_server_id}... (Enter to send)` : 'Message... (Enter to send)'}
                                            autoSize={{ minRows: 1, maxRows: 4 }}
                                            disabled={sending}
                                            className="message-input"
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SendOutlined />}
                                            onClick={() => sendMessage(inputValue, null, setInputValue, setSending)}
                                            loading={sending}
                                            disabled={!inputValue.trim()}
                                            className="send-button"
                                        >
                                            Send
                                        </Button>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Thread panel */}
                            {showThreadPane && (() => {
                                const rootMsg = activeChannel.messages.find(m => m.message_id === activeThreadId);
                                const threadMessages = rootMsg ? collectThread(activeThreadId, activeChannel.messages) : [];
                                return (
                                    <div style={{
                                        flex: isMobile ? 1 : 2,
                                        width: isMobile ? '100%' : undefined,
                                        borderLeft: isMobile ? 'none' : '1px solid var(--border-color)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexShrink: 0,
                                        background: 'var(--bg-primary)',
                                    }}>
                                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {isMobile && (
                                                <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => setActiveThreadId(null)} style={{ padding: 0 }} />
                                            )}
                                            <Text strong style={{ fontSize: 13, flex: 1 }}>Thread</Text>
                                            {!isMobile && (
                                                <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setActiveThreadId(null)} style={{ padding: 0 }} />
                                            )}
                                        </div>
                                        {rootMsg && (
                                            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                                                {renderBubble(rootMsg)}
                                            </div>
                                        )}
                                        <div className="messages-container" style={{ flex: 1 }}>
                                            {threadMessages.length === 0 ? (
                                                <div style={{ padding: '20px 12px', textAlign: 'center' }}>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>No replies yet</Text>
                                                </div>
                                            ) : (
                                                threadMessages.map(msg => renderBubble(msg))
                                            )}
                                            <div ref={threadEndRef} />
                                        </div>
                                        <div className="input-container" style={{ padding: '8px', borderTop: '1px solid var(--border-color)' }}>
                                            <EmojiPicker
                                                disabled={sendingThread}
                                                onSelect={emoji => insertAtCaret(threadInputRef, threadInputValue, setThreadInputValue, emoji)}
                                            />
                                            <TextArea
                                                ref={threadInputRef}
                                                value={threadInputValue}
                                                onChange={e => setThreadInputValue(e.target.value)}
                                                onKeyPress={handleThreadKeyPress}
                                                placeholder="Reply..."
                                                autoSize={{ minRows: 1, maxRows: 4 }}
                                                disabled={sendingThread}
                                                className="message-input"
                                            />
                                            <Button
                                                type="primary"
                                                icon={<SendOutlined />}
                                                onClick={() => sendMessage(threadInputValue, activeThreadId, setThreadInputValue, setSendingThread)}
                                                loading={sendingThread}
                                                disabled={!threadInputValue.trim()}
                                                className="send-button"
                                            />
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </>
                )}
            </div>
            )}
        </div>
        </div>
        </>
    );
}

export default Messages;
